import { useCallback, useEffect, useState } from "react"
import { ButtonPrimary, ButtonTertiary } from "@nova/component-btn"
import * as Icons from "@nova/component-icons"
import { useRouter } from "pages"
import {
    useCreateMaintenanceTemplate,
    invalidateMaintenanceTemplates,
} from "features/pilot-tracking/maintenance/MaintenanceTemplateQueries"
import { getGenericWorkspacePage, getGenericWorkspacePageHeader } from "../WorkspaceRoutes"
import { MaintenanceTemplateForm } from "./MaintenanceTemplateForm"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { setGlobalNotificationContext } from "user/profile/slice"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import {
    resetForm,
    selectFormAsCreatable,
    selectSubmitting,
    setSubmitting,
    updateForm,
} from "features/pilot-tracking/maintenance/slice"
import { createQuery } from "@nova/queryable"
import { usePilotTrackingQuery } from "features/pilot-tracking/PilotTrackingQueries"

const templateCountQuery = createQuery({
    resource: "maintenance_templates",
    select: ["id"],
})

function CancelButton() {
    const router = useRouter("/workspace/:workspaceId")
    const submitting = useAppSelector(selectSubmitting)
    return (
        <ButtonTertiary
            title="Cancel"
            onClick={() => router.navigate({ path: "/fleet/maintenance" })}
            disabled={submitting}
        />
    )
}

function CreateTemplateSubmitButton() {
    const form = useAppSelector(selectFormAsCreatable)
    const { createMaintenanceTemplate } = useCreateMaintenanceTemplate()
    const dispatch = useAppDispatch()
    const router = useRouter("/workspace/:workspaceId")
    const submitting = useAppSelector(selectSubmitting)

    const handleSubmit = useCallback(async () => {
        dispatch(setSubmitting(true))
        try {
            await createMaintenanceTemplate(form)
            invalidateMaintenanceTemplates()
            dispatch(
                setGlobalNotificationContext({
                    message: `${form.name} has been created.`,
                    status: GlobalNotificationStatus.success,
                    timeoutInMs: 5000,
                })
            )
            router.navigate({ path: "/fleet/maintenance" })
        } catch {
            dispatch(
                setGlobalNotificationContext({
                    message: "Failed to create the template. Please try again.",
                    status: GlobalNotificationStatus.error,
                    timeoutInMs: 5000,
                })
            )
        } finally {
            dispatch(setSubmitting(false))
        }
    }, [form, createMaintenanceTemplate, dispatch, router])

    return (
        <ButtonPrimary
            title="Create Template"
            RightIcon={Icons.Checkmark}
            onClick={handleSubmit}
            disabled={submitting}
        />
    )
}

export const CreateMaintenanceTemplatePage = getGenericWorkspacePage({
    Header: getGenericWorkspacePageHeader({
        title: "Create Maintenance Template",
        rightItems: [CancelButton, CreateTemplateSubmitButton],
        border: true,
    }),
    Content: CreateMaintenanceTemplatePageContent,
    maxWidth: 1200,
})

function CreateMaintenanceTemplatePageContent() {
    const dispatch = useAppDispatch()
    const [isFormReady, setIsFormReady] = useState(false)
    const { data: templates, isLoading } = usePilotTrackingQuery(templateCountQuery)

    useEffect(() => {
        if (!isLoading && templates && !isFormReady) {
            dispatch(resetForm())
            dispatch(updateForm({ key: "name", value: `Service #${templates.length + 1}` }))
            setIsFormReady(true)
        }
    }, [isLoading, templates, isFormReady, dispatch])

    if (!isFormReady) {
        return (
            <div className="w-full h-full flex justify-center items-center">
                <div className="loader" />
            </div>
        )
    }

    return <MaintenanceTemplateForm />
}
